/**
 * Renders the main sign-in screen and the related handling.
 *
 * Currently the following login methods are supported:
 *  - Login with Google
 *  - Login with username/password
 *
 * In addition, a link to the Moodle course is shown (if avaialble in the local storage)
 */

import React, {useEffect, useState} from "react";
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';

import {useStyles} from "./InitPassword.css";
import AppButton from "../../elements/buttons/AppButton";
import {useIntl} from "react-intl";
import logo from 'assets/judged_black_bg.png';
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import useInitPassword from "./useInitPassword";
import {LinearProgress} from "@mui/material";

export default function InitPassword() {
    const classes = useStyles()

    // Internationalization for simple strings
    const intl = useIntl();

    // Retrieve get parameter from the link (for retrieving the token + email address)
    const searchParams = new URLSearchParams(window.location.search)
    const email = searchParams.get('email')
    const token = searchParams.get('token')

    // States for textfields
    const [password, setPassword] = useState('')
    const [passwordRepeat, setPasswordRepeat] = useState('')

    const [saveButtonEnabled, setSaveButtonEnabled] = useState(false)

    // Hook for handling the request
    const {initPassword, loading} = useInitPassword()

    useEffect(() => {
        setSaveButtonEnabled(password && passwordRepeat && password === passwordRepeat)
    }, [password, passwordRepeat])

    const handleChangePassword = (event) => {
        setPassword(event.target.value)
    }

    const handleChangePasswordRepeat = (event) => {
        setPasswordRepeat(event.target.value)
    }

    // Special handling, because we want that the user can press "ENTER" to trigger the login as well
    const handleSubmit = (e) => {
        e.preventDefault()
        initPassword(token, email, password)
    }

    const getPasswordMatchInfo = () => {
        if (password && passwordRepeat) {
            if (password !== passwordRepeat || password.length < 6) {
                return <ErrorIcon className={classes.errorIcon}/>
            } else {
                return <CheckCircleIcon className={classes.successIcon}/>
            }
        }
        return ''
    }

    return (
        <div>
            {loading && <LinearProgress/>}
            <Container component="main" maxWidth="sm">
                <div className={classes.header}>
                    <div>
                        <img className={classes.logo} src={logo} alt='App logo'/>
                    </div>
                    <Typography component="h1" variant="h5">
                        {intl.formatMessage({
                            id: 'app.users.setPassword.header',
                            defaultMessage: 'Password Initialization'
                        })}
                    </Typography>
                </div>

                <form className={classes.passwordInitSection} onSubmit={handleSubmit}>

                    {/* Email indication for which account the password will be set */}
                    <Typography align="center" component="p">
                        {email}
                    </Typography>

                    {/* Password */}
                    <TextField variant="outlined" margin="normal" fullWidth required value={password}
                               onChange={handleChangePassword}
                               id="reg-user-password"
                               type="password"
                               label={intl.formatMessage({
                                   id: 'app.users.setPassword.form.password.label',
                                   defaultMessage: 'Password'
                               })}/>

                    {/* Password repeat  */}
                    <TextField variant="outlined" margin="normal" fullWidth required value={passwordRepeat}
                               onChange={handleChangePasswordRepeat}
                               id="reg-user-passwordRepeat"
                               type="password"
                               label={intl.formatMessage({
                                   id: 'app.users.setPassword.form.passwordRepeat.label',
                                   defaultMessage: 'Password Repeat'
                               })}
                               InputProps={{endAdornment: getPasswordMatchInfo()}}/>

                    <AppButton
                        label={intl.formatMessage({
                            id: 'app.users.setPassword.form.setPasswordButton.label',
                            defaultMessage: 'Set Password'
                        })}
                        disabled={!saveButtonEnabled}/>
                </form>
            </Container>
        </div>
    );
}
